import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { AppUsersPage } from '../../../../api/admin';
import {
  adminUsersQueryOptions,
  appUserSortFields,
  createAppUser,
  updateAppUser,
  withUpdatedUser,
} from '../../../../api/admin';
import type { CreateUserDto, UpdateUserDto } from '../../../../api/generated';
import type { UsersQuery } from '../../../../components/admin/UsersTab';
import { UsersTab } from '../../../../components/admin/UsersTab';
import { ErrorMessage } from '../../../../components/common/ErrorMessage';

const searchSchema = z.object({
  page: z.number().int().min(1).optional().default(1),
  q: z.string().optional().default(''),
  sortBy: z.enum(appUserSortFields).optional().default('updatedAt'),
  sortDir: z.enum(['asc', 'desc']).optional().default('desc'),
});

export const Route = createFileRoute('/_auth/admin/users/')({
  validateSearch: searchSchema,
  component: AdminUsersPage,
});

const PAGE_SIZE = 25;

function AdminUsersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isLoading } = useQuery(
    adminUsersQueryOptions({
      q: search.q || undefined,
      sortBy: search.sortBy,
      sortDir: search.sortDir,
      page: search.page,
      limit: PAGE_SIZE,
    }),
  );

  const handleQueryChange = (patch: Partial<UsersQuery>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  const updateMutation = useMutation({
    mutationFn: ({ oid, body }: { oid: string; body: UpdateUserDto }) =>
      updateAppUser(oid, body),
    onSuccess: (updated) => {
      // The edited row refreshes where it sits. Refetching here would reorder
      // the list under the operator's cursor: the default sort is
      // `updatedAt desc` and every edit bumps `updatedAt`.
      queryClient.setQueriesData<AppUsersPage>(
        { queryKey: ['admin', 'users'] },
        (page) => (page ? withUpdatedUser(page, updated) : page),
      );
      // Still marked stale: the true server order returns with the next sort,
      // page change or remount — just not mid-edit.
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'users'],
        refetchType: 'none',
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateUserDto) => createAppUser(body),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const mutationError = updateMutation.error ?? createMutation.error;
  const errorMsg =
    mutationError instanceof Error ? mutationError.message : null;

  return (
    <section className="epi-card">
      <div className="epi-card-head">
        <span className="num">{t('admin.users.cardHead.num')}</span>
        <span className="title">{t('admin.users.cardHead.title')}</span>
        <span className="meta">{t('admin.users.cardHead.meta')}</span>
      </div>
      {errorMsg && (
        <div className="mb-4">
          <ErrorMessage message={errorMsg} size="xs" />
        </div>
      )}
      <UsersTab
        data={data}
        isLoading={isLoading}
        query={search}
        onQueryChange={handleQueryChange}
        onUpdate={(oid, body) => updateMutation.mutate({ oid, body })}
        onCreate={(body) => createMutation.mutateAsync(body)}
        isCreating={createMutation.isPending}
      />
    </section>
  );
}
